import { privateApi } from '@/config/axios.config';

/**
 * #1060 — el lado del front de la aceptación legal (Ley 172-13).
 *
 * El backend ya sabía hacer todo esto y estaba desplegado: persiste el
 * consentimiento en el alta, expone `GET /auth/legal-status` y registra la
 * aceptación retroactiva por `POST /auth/accept-legal`. Medido en producción
 * el 2026-08-16: la ruta contesta 401 (existe y está guardada), no 404.
 *
 * Y aun así había **94 usuarios y 0 pruebas de consentimiento**, porque nadie
 * del lado del navegador llamaba jamás a ninguna de las dos. Ese es el hueco
 * que cierran estos archivos: no falta código que sepa guardar, falta alguien
 * que pregunte.
 */

/** Espejo de `LegalStatusDTO` del backend (apps/api/.../dto/auth.dto.ts). */
export interface LegalStatus {
  /** true si faltan términos/privacidad, o si la versión aceptada caducó. */
  pending: boolean;
  termsAcceptedAt: string | null;
  privacyAcceptedAt: string | null;
  termsVersion: string | null;
  privacyVersion: string | null;
  /**
   * Las versiones VIGENTES, tal como las manda el server.
   *
   * No se hardcodean acá a propósito: si el front fija la versión, subir el
   * texto legal deja de tener efecto sobre quién tiene que volver a aceptar, y
   * volvemos al mismo silencio por otra puerta.
   */
  currentTermsVersion: string;
  currentPrivacyVersion: string;
}

export const legalApi = {
  /** Qué le falta a este usuario. El userId sale del JWT, nunca del cliente. */
  async getStatus(): Promise<LegalStatus> {
    const { data } = await privateApi.get<LegalStatus>('/auth/legal-status');
    return data;
  },

  /**
   * Registra la aceptación. El backend exige que ambos sean `true`
   * (`@Equals(true)`): un `false` acá no es "no acepto" —para eso está revocar,
   * que es otro flujo— y guardarlo crearía una fila que parece consentimiento
   * y no lo es.
   */
  async accept(): Promise<LegalStatus> {
    const { data } = await privateApi.post<LegalStatus>('/auth/accept-legal', {
      acceptTerms: true,
      acceptPrivacy: true,
    });
    return data;
  },
};
